/** Low-cardinality device hints plus a random browser-profile ID, never a fingerprint. */
export interface ClientContext {
  browser_id?: string;
  viewport_width: number;
  viewport_height: number;
  screen_width: number;
  screen_height: number;
  max_touch_points: number;
  pointer: 'fine' | 'coarse' | 'none';
  display_mode: 'browser' | 'standalone';
  timezone_offset_minutes: number;
  app_build?: string;
}

export const CLIENT_CONTEXT_HEADER = 'X-Client-Context';
const BROWSER_ID_KEY = 'erp24.activity.browser-id';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
let memoryId: string | undefined;

function browserId(): string | undefined {
  try {
    const stored = window.localStorage.getItem(BROWSER_ID_KEY);
    if (stored && UUID_RE.test(stored)) return stored;
    memoryId ??= crypto.randomUUID();
    window.localStorage.setItem(BROWSER_ID_KEY, memoryId);
  } catch {
    // Storage can be disabled. An in-memory ID then lasts only this document.
    try {
      memoryId ??= crypto.randomUUID();
    } catch {
      return undefined;
    }
  }
  return memoryId;
}

/** Snapshot at fact creation, so an offline queue retains the original viewport/build. */
export function collectClientContext(): ClientContext | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    // Treat optional platform capabilities as partial at the runtime boundary.
    const media = window.matchMedia as
      | ((query: string) => Pick<MediaQueryList, 'matches'> | undefined)
      | undefined;
    const matches = (query: string): boolean => media?.(query)?.matches ?? false;
    const browser_id = browserId();
    const build = import.meta.env.VITE_DEPLOY_SHA?.trim();
    return {
      ...(browser_id ? { browser_id } : {}),
      viewport_width: Math.round(window.innerWidth),
      viewport_height: Math.round(window.innerHeight),
      screen_width: Math.round(window.screen.width),
      screen_height: Math.round(window.screen.height),
      max_touch_points: (navigator as Partial<Navigator>).maxTouchPoints ?? 0,
      pointer: matches('(pointer: coarse)')
        ? 'coarse'
        : matches('(pointer: fine)')
          ? 'fine'
          : 'none',
      display_mode:
        matches('(display-mode: standalone)') ||
        (navigator as Navigator & { standalone?: boolean }).standalone === true
          ? 'standalone'
          : 'browser',
      timezone_offset_minutes: new Date().getTimezoneOffset(),
      ...(build && /^[a-f0-9]{7,40}$/.test(build) ? { app_build: build } : {}),
    };
  } catch {
    return undefined;
  }
}

/** Works before auth/tracker startup; never sends the browser ID to an external origin. */
export function getClientContextHeader(url: string): string | null {
  try {
    const target = new URL(url, window.location.href);
    if (
      target.origin !== window.location.origin ||
      !/^\/api\/(web-admin|erp)\//.test(target.pathname)
    )
      return null;
    const context = collectClientContext();
    return context ? JSON.stringify(context) : null;
  } catch {
    return null;
  }
}
